import { useEffect, useRef, useState, type DragEvent, type KeyboardEvent } from "react";
import type { LucideIcon } from "lucide-react";
import { GripVertical, Plus, RotateCcw, X } from "lucide-react";
import { Button, Modal } from "@fawkes/ui";

// De tegelvensters staan in een portal, buiten het klantscherm; hun opmaak staat bij die van het scherm.
import "../PensionKlant.css";
import type { PensionDier } from "../pensionKlantTypes";
import type { Context } from "./hulp";
import {
  LIJSTVELDEN,
  TEGEL_PER_SLEUTEL,
  standaardInstelling,
  standaardLijst,
  standaardVoorkeur,
  type DieroverzichtVoorkeur,
  type TegelInstelling,
  type TegelSleutel,
  type Tint,
} from "./tegelDefinities";
import { Rondje, TEGEL_COMPONENT, type TegelActies } from "./Tegels";

/**
 * De twee vensters om het dieroverzicht in te stellen: per tegel (wat staat erin en hoe) via het
 * tandwiel, en voor alle tegels samen (welke en in welke volgorde) via "Tegels instellen".
 *
 * <p>Beide zijn een raster waarin je de onderdelen sleept: naar "Op de tegel" om ze te tonen, binnen
 * het raster om de volgorde te bepalen, en terug naar "Beschikbaar" om ze weg te halen. Wie niet sleept
 * gebruikt de + en × op een blok, of de pijltjestoetsen op de greep.
 *
 * <p>Alles is eerst een concept: pas "Opslaan" bewaart het, per gebruiker. "Standaard herstellen" zet
 * het concept terug; ook dat wordt pas bij "Opslaan" bewaard.
 */

export interface Blok {
  sleutel: string;
  label: string;
  icoon: LucideIcon;
  tint: Tint;
}

interface RasterTeksten {
  aan: string;
  uit: string;
  aanLeeg: string;
  uitLeeg: string;
  voegToe: (label: string) => string;
  haalWeg: (label: string) => string;
}

/**
 * Twee rasters met blokken: wat erop staat, en wat er nog bij kan. Slepen verplaatst een blok binnen
 * het bovenste raster, van onder naar boven (toevoegen op de plek waar je loslaat) of van boven naar
 * onder (weghalen).
 */
export function OnderdelenRaster({
  aan,
  uit,
  kolommen,
  teksten,
  onChange,
  ctx,
}: {
  aan: Blok[];
  uit: Blok[];
  kolommen: number;
  teksten: RasterTeksten;
  /** De nieuwe volgorde van wat erop staat. */
  onChange: (aan: string[]) => void;
  ctx: Context;
}) {
  const { t } = ctx;
  const [sleept, setSleept] = useState<{ sleutel: string; van: "aan" | "uit" } | null>(null);
  const [doel, setDoel] = useState<number | "uit" | null>(null);
  const [melding, setMelding] = useState("");
  const sleutels = aan.map((b) => b.sleutel);
  const naam = (s: string) => [...aan, ...uit].find((b) => b.sleutel === s)?.label ?? s;

  const zet = (nieuw: string[], bericht: string) => {
    onChange(nieuw);
    setMelding(bericht);
  };

  const plaats = (sleutel: string, op: number) => {
    const zonder = sleutels.filter((s) => s !== sleutel);
    const index = Math.max(0, Math.min(op, zonder.length));
    zonder.splice(index, 0, sleutel);
    zet(zonder, t("pensionKlant.instellen.verplaatst", { args: [naam(sleutel), index + 1, zonder.length] }));
  };

  const haalWeg = (sleutel: string) =>
    zet(
      sleutels.filter((s) => s !== sleutel),
      t("pensionKlant.instellen.weggehaald", { args: [naam(sleutel)] }),
    );

  const begin = (e: DragEvent, sleutel: string, van: "aan" | "uit") => {
    setSleept({ sleutel, van });
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", sleutel);
    }
  };
  const klaar = () => {
    setSleept(null);
    setDoel(null);
  };

  const loslatenOp = (e: DragEvent, op: number | "einde" | "uit") => {
    e.preventDefault();
    e.stopPropagation();
    if (!sleept) return klaar();
    if (op === "uit") {
      if (sleept.van === "aan") haalWeg(sleept.sleutel);
    } else {
      const index = op === "einde" ? sleutels.length : op;
      // Binnen het raster naar achteren schuiven: de lege plek van het blok zelf telt niet mee.
      const van = sleutels.indexOf(sleept.sleutel);
      plaats(sleept.sleutel, van !== -1 && van < index ? index - 1 : index);
    }
    klaar();
  };

  const opToets = (e: KeyboardEvent, sleutel: string, plek: number) => {
    const stap = { ArrowLeft: -1, ArrowUp: -kolommen, ArrowRight: 1, ArrowDown: kolommen }[e.key];
    if (stap === undefined) return;
    e.preventDefault();
    plaats(sleutel, plek + stap);
  };

  return (
    <div className="pk-raster-instellen" data-sleept={sleept ? "" : undefined}>
      <h3>{teksten.aan}</h3>
      <ol
        className="pk-blokken pk-blokken--aan"
        style={{ gridTemplateColumns: `repeat(${kolommen}, minmax(0, 1fr))` }}
        aria-label={teksten.aan}
        onDragOver={(e) => {
          if (!sleept) return;
          e.preventDefault();
          if (doel === null || doel === "uit") setDoel(sleutels.length);
        }}
        onDrop={(e) => loslatenOp(e, "einde")}
      >
        {aan.map((b, plek) => (
          <li
            key={b.sleutel}
            className="pk-blok"
            data-sleept={sleept?.sleutel === b.sleutel || undefined}
            data-doel={doel === plek && sleept?.sleutel !== b.sleutel ? "" : undefined}
            draggable
            onDragStart={(e) => begin(e, b.sleutel, "aan")}
            onDragEnd={klaar}
            onDragOver={(e) => {
              if (!sleept) return;
              e.preventDefault();
              e.stopPropagation();
              setDoel(plek);
            }}
            onDrop={(e) => loslatenOp(e, plek)}
          >
            <button
              type="button"
              className="pk-blok__greep"
              aria-label={t("pensionKlant.instellen.verplaatsAria", { args: [b.label] })}
              title={t("pensionKlant.instellen.sleep")}
              onKeyDown={(e) => opToets(e, b.sleutel, plek)}
            >
              <GripVertical size={15} aria-hidden="true" />
            </button>
            <Rondje icoon={b.icoon} tint={b.tint} />
            <span className="pk-blok__naam">{b.label}</span>
            <button
              type="button"
              className="pk-blok__knop"
              aria-label={teksten.haalWeg(b.label)}
              title={teksten.haalWeg(b.label)}
              onClick={() => haalWeg(b.sleutel)}
            >
              <X size={15} aria-hidden="true" />
            </button>
          </li>
        ))}
        {aan.length === 0 && <li className="pk-blokken__leeg">{teksten.aanLeeg}</li>}
      </ol>

      <h3>{teksten.uit}</h3>
      <ul
        className="pk-blokken pk-blokken--uit"
        style={{ gridTemplateColumns: `repeat(${kolommen}, minmax(0, 1fr))` }}
        aria-label={teksten.uit}
        data-doel={doel === "uit" && sleept?.van === "aan" ? "" : undefined}
        onDragOver={(e) => {
          if (sleept?.van !== "aan") return;
          e.preventDefault();
          setDoel("uit");
        }}
        onDragLeave={() => doel === "uit" && setDoel(null)}
        onDrop={(e) => loslatenOp(e, "uit")}
      >
        {uit.map((b) => (
          <li
            key={b.sleutel}
            className="pk-blok pk-blok--uit"
            data-sleept={sleept?.sleutel === b.sleutel || undefined}
            draggable
            onDragStart={(e) => begin(e, b.sleutel, "uit")}
            onDragEnd={klaar}
          >
            <Rondje icoon={b.icoon} tint={b.tint} />
            <span className="pk-blok__naam">{b.label}</span>
            <button
              type="button"
              className="pk-blok__knop pk-blok__knop--erbij"
              aria-label={teksten.voegToe(b.label)}
              title={teksten.voegToe(b.label)}
              onClick={() => plaats(b.sleutel, sleutels.length)}
            >
              <Plus size={15} aria-hidden="true" />
            </button>
          </li>
        ))}
        {uit.length === 0 && <li className="pk-blokken__leeg">{teksten.uitLeeg}</li>}
      </ul>
      <p className="pk-raster-instellen__melding" aria-live="polite">
        {melding}
      </p>
    </div>
  );
}

function Voet({
  ctx,
  bezig,
  fout,
  onStandaard,
  onAnnuleren,
  onOpslaan,
}: {
  ctx: Context;
  bezig: boolean;
  fout: boolean;
  onStandaard: () => void;
  onAnnuleren: () => void;
  onOpslaan: () => void;
}) {
  const { t } = ctx;
  return (
    <div className="pk-instellen__voet">
      <button type="button" className="pk-instellen__standaard" onClick={onStandaard} disabled={bezig}>
        <RotateCcw size={15} aria-hidden="true" /> {t("pensionKlant.instellen.standaard")}
      </button>
      {fout && (
        <p className="fawkes-error" role="alert">
          {t("pensionKlant.instellen.mislukt")}
        </p>
      )}
      <span className="pk-instellen__rechts">
        <Button variant="secondary" onClick={onAnnuleren} disabled={bezig}>
          {t("common.cancel")}
        </Button>
        <Button variant="primary" onClick={onOpslaan} disabled={bezig}>
          {t("common.save")}
        </Button>
      </span>
    </div>
  );
}

/** Opslaan met een foutmelding als het misgaat, en het venster dicht als het lukt. */
function useOpslaan(onOpslaan: () => Promise<void>, onClose: () => void) {
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState(false);
  const opslaan = async () => {
    setBezig(true);
    setFout(false);
    try {
      await onOpslaan();
      onClose();
    } catch {
      setFout(true);
    } finally {
      setBezig(false);
    }
  };
  return { bezig, fout, setFout, opslaan };
}

/** Het voorbeeld is de echte tegel, maar je kunt er niets in aanklikken: het is een plaatje. */
function Voorbeeld({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    ref.current?.setAttribute("inert", "");
  }, []);
  return (
    <div ref={ref} className="pk-voorbeeld" aria-hidden="true">
      {children}
    </div>
  );
}

// ── Eén tegel: wat en hoe ────────────────────────────────────────────────────────────────────────

const GEEN_ACTIES: TegelActies = {
  openDossierTegel: () => {}, naarSubtab: () => {}, naarPension: () => {}, gewijzigd: () => {},
};

export function TegelInstellen({
  sleutel,
  voorkeur,
  dier,
  ctx,
  onClose,
  onOpslaan,
}: {
  sleutel: TegelSleutel | null;
  voorkeur: DieroverzichtVoorkeur;
  /** Het dier in het voorbeeld: het dier dat nu open staat. */
  dier: PensionDier;
  ctx: Context;
  onClose: () => void;
  onOpslaan: (v: DieroverzichtVoorkeur) => Promise<void>;
}) {
  const { t } = ctx;
  const def = sleutel ? TEGEL_PER_SLEUTEL[sleutel] : null;
  const [concept, setConcept] = useState<TegelInstelling | null>(null);

  useEffect(() => {
    if (sleutel) setConcept(voorkeur.tegels.find((x) => x.sleutel === sleutel) ?? standaardInstelling(TEGEL_PER_SLEUTEL[sleutel]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sleutel]);

  const { bezig, fout, setFout, opslaan } = useOpslaan(async () => {
    if (!sleutel || !concept) return;
    await onOpslaan({ ...voorkeur, tegels: voorkeur.tegels.map((x) => (x.sleutel === sleutel ? concept : x)) });
  }, onClose);

  if (!def || !concept) return null;
  const titel = t(def.titel);
  const blok = (s: string): Blok => {
    const r = def.regels!.find((x) => x.sleutel === s)!;
    return { sleutel: s, label: t(r.label), icoon: r.icoon, tint: r.tint };
  };
  const aan = (concept.regels ?? []).map(blok);
  const uit = (def.regels ?? []).filter((r) => !(concept.regels ?? []).includes(r.sleutel)).map((r) => blok(r.sleutel));
  const Component = TEGEL_COMPONENT[def.sleutel];

  return (
    <Modal
      open={sleutel !== null}
      title={t("pensionKlant.instellen.tegelTitel", { args: [titel] })}
      onClose={onClose}
      size="wide"
      footer={
        <Voet
          ctx={ctx}
          bezig={bezig}
          fout={fout}
          onStandaard={() => {
            setFout(false);
            setConcept(standaardInstelling(def));
          }}
          onAnnuleren={onClose}
          onOpslaan={opslaan}
        />
      }
    >
      <div className="pk-instellen pk-instellen--tegel">
        <div className="pk-instellen__kolom">
          {def.regels && (
            <>
              <p className="pk-instellen__uitleg">{t("pensionKlant.instellen.sleepUitleg")}</p>
              <OnderdelenRaster
                aan={aan}
                uit={uit}
                kolommen={2}
                ctx={ctx}
                onChange={(regels) => setConcept({ ...concept, regels })}
                teksten={{
                  aan: t("pensionKlant.instellen.opDeTegel"),
                  uit: t("pensionKlant.instellen.beschikbaar"),
                  aanLeeg: t("pensionKlant.instellen.sleepHierheen"),
                  uitLeeg: t("pensionKlant.instellen.allesErop"),
                  voegToe: (l) => t("pensionKlant.instellen.voegToeAria", { args: [l] }),
                  haalWeg: (l) => t("pensionKlant.instellen.haalWegAria", { args: [l] }),
                }}
              />
            </>
          )}
          {def.opties && (
            <section>
              <h3>{t("pensionKlant.instellen.hoe")}</h3>
              {def.opties.map((o) => (
                <fieldset key={o.sleutel} className="pk-instellen__optie">
                  <legend>{t(o.label)}</legend>
                  <div className="pk-segment" role="radiogroup" aria-label={t(o.label)}>
                    {o.waarden.map((w) => (
                      <button
                        key={String(w.waarde)}
                        type="button"
                        role="radio"
                        aria-checked={(concept.opties?.[o.sleutel] ?? o.standaard) === w.waarde}
                        onClick={() => setConcept({ ...concept, opties: { ...concept.opties, [o.sleutel]: w.waarde } })}
                      >
                        {/^\d+$/.test(w.label) ? w.label : t(w.label)}
                      </button>
                    ))}
                  </div>
                </fieldset>
              ))}
            </section>
          )}
        </div>
        <aside className="pk-instellen__voorbeeld" aria-label={t("pensionKlant.instellen.voorbeeld")}>
          <h3>{t("pensionKlant.instellen.voorbeeld")}</h3>
          <Voorbeeld>
            <Component dier={dier} inst={concept} ctx={ctx} acties={GEEN_ACTIES} onInstellen={() => {}} />
          </Voorbeeld>
        </aside>
      </div>
    </Modal>
  );
}

// ── Alle tegels: welke en in welke volgorde ──────────────────────────────────────────────────────

export function TegelsInstellen({
  open,
  voorkeur,
  ctx,
  onClose,
  onOpslaan,
}: {
  open: boolean;
  voorkeur: DieroverzichtVoorkeur;
  ctx: Context;
  onClose: () => void;
  onOpslaan: (v: DieroverzichtVoorkeur) => Promise<void>;
}) {
  const { t } = ctx;
  const [concept, setConcept] = useState<DieroverzichtVoorkeur>(voorkeur);

  useEffect(() => {
    if (open) setConcept(voorkeur);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const { bezig, fout, setFout, opslaan } = useOpslaan(() => onOpslaan(concept), onClose);

  const blok = (s: TegelSleutel): Blok => {
    const d = TEGEL_PER_SLEUTEL[s];
    return { sleutel: s, label: t(d.titel), icoon: d.icoon, tint: d.tint };
  };
  const aan = concept.tegels.filter((x) => x.zichtbaar).map((x) => blok(x.sleutel));
  const uit = concept.tegels.filter((x) => !x.zichtbaar).map((x) => blok(x.sleutel));

  /** De nieuwe volgorde van zichtbare tegels; de verborgen houden hun instellingen en komen erachter. */
  const zetZichtbaar = (zichtbaar: string[]) => {
    const per = new Map(concept.tegels.map((x) => [x.sleutel, x]));
    const voor = zichtbaar.map((s) => ({ ...per.get(s as TegelSleutel)!, zichtbaar: true }));
    const na = concept.tegels.filter((x) => !zichtbaar.includes(x.sleutel)).map((x) => ({ ...x, zichtbaar: false }));
    setConcept({ ...concept, tegels: [...voor, ...na] });
  };

  return (
    <Modal
      open={open}
      title={t("pensionKlant.instellen.tegelsTitel")}
      onClose={onClose}
      size="wide"
      footer={
        <Voet
          ctx={ctx}
          bezig={bezig}
          fout={fout}
          onStandaard={() => {
            setFout(false);
            setConcept(standaardVoorkeur());
          }}
          onAnnuleren={onClose}
          onOpslaan={opslaan}
        />
      }
    >
      <div className="pk-instellen">
        <p className="pk-instellen__uitleg">{t("pensionKlant.instellen.tegelsUitleg")}</p>
        <OnderdelenRaster
          aan={aan}
          uit={uit}
          kolommen={4}
          ctx={ctx}
          onChange={zetZichtbaar}
          teksten={{
            aan: t("pensionKlant.instellen.zichtbaar"),
            uit: t("pensionKlant.instellen.verborgen"),
            aanLeeg: t("pensionKlant.instellen.sleepHierheen"),
            uitLeeg: t("pensionKlant.instellen.nietsVerborgen"),
            voegToe: (l) => t("pensionKlant.instellen.toonAria", { args: [l] }),
            haalWeg: (l) => t("pensionKlant.instellen.verbergAria", { args: [l] }),
          }}
        />
      </div>
    </Modal>
  );
}

/**
 * Welke velden er op een kaartje in de dierenlijst staan, en in welke volgorde. Zelfde raster als de
 * tegelvensters: slepen, of de + en × op een blok.
 */
export function LijstInstellen({
  open,
  voorkeur,
  ctx,
  onClose,
  onOpslaan,
}: {
  open: boolean;
  voorkeur: DieroverzichtVoorkeur;
  ctx: Context;
  onClose: () => void;
  onOpslaan: (v: DieroverzichtVoorkeur) => Promise<void>;
}) {
  const { t } = ctx;
  const [concept, setConcept] = useState<string[]>(voorkeur.lijst);

  useEffect(() => {
    if (open) setConcept(voorkeur.lijst);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const { bezig, fout, setFout, opslaan } = useOpslaan(() => onOpslaan({ ...voorkeur, lijst: concept }), onClose);

  const blok = (sleutel: string): Blok => {
    const d = LIJSTVELDEN.find((x) => x.sleutel === sleutel)!;
    return { sleutel, label: t(d.label), icoon: d.icoon, tint: d.tint };
  };
  const aan = concept.filter((s) => LIJSTVELDEN.some((x) => x.sleutel === s)).map(blok);
  const uit = LIJSTVELDEN.filter((x) => !concept.includes(x.sleutel)).map((x) => blok(x.sleutel));

  return (
    <Modal
      open={open}
      title={t("pensionKlant.instellen.lijstTitel")}
      onClose={onClose}
      size="wide"
      footer={
        <Voet
          ctx={ctx}
          bezig={bezig}
          fout={fout}
          onStandaard={() => {
            setFout(false);
            setConcept(standaardLijst());
          }}
          onAnnuleren={onClose}
          onOpslaan={opslaan}
        />
      }
    >
      <div className="pk-instellen">
        <p className="pk-instellen__uitleg">{t("pensionKlant.instellen.lijstUitleg")}</p>
        <OnderdelenRaster
          aan={aan}
          uit={uit}
          kolommen={4}
          ctx={ctx}
          onChange={setConcept}
          teksten={{
            aan: t("pensionKlant.instellen.opKaartje"),
            uit: t("pensionKlant.instellen.beschikbaar"),
            aanLeeg: t("pensionKlant.instellen.sleepHierheen"),
            uitLeeg: t("pensionKlant.instellen.nietsBeschikbaar"),
            voegToe: (l) => t("pensionKlant.instellen.opKaartjeAria", { args: [l] }),
            haalWeg: (l) => t("pensionKlant.instellen.vanKaartjeAria", { args: [l] }),
          }}
        />
      </div>
    </Modal>
  );
}
